import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";

const EMAIL_COOLDOWN_SECONDS = 30;
const STORAGE_KEY = "email_verification_sent";

interface EmailVerificationData {
  sentAt: number;
}

export function useEmailVerification() {
  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [showOTPForm, setShowOTPForm] = useState(false);

  // Fonctions utilitaires (sans useCallback car React 19 optimise automatiquement)
  const getEmailVerificationData = (email: string): EmailVerificationData | null => {
    const storageKey = `${STORAGE_KEY}_${email}`;
    const data = localStorage.getItem(storageKey);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  };

  const saveEmailVerificationData = (email: string, sentAt: number) => {
    const storageKey = `${STORAGE_KEY}_${email}`;
    localStorage.setItem(storageKey, JSON.stringify({ sentAt }));
  };

  const getRemainingCooldown = (email: string): number => {
    const data = getEmailVerificationData(email);
    if (!data?.sentAt) return 0;

    const now = Date.now();
    const elapsed = Math.floor((now - data.sentAt) / 1000);
    const remaining = EMAIL_COOLDOWN_SECONDS - elapsed;

    return Math.max(0, remaining);
  };

  const clearEmailVerificationData = (email: string) => {
    const storageKey = `${STORAGE_KEY}_${email}`;
    localStorage.removeItem(storageKey);
  };

  // Initialiser l'état basé sur les données stockées
  const initializeFromEmail = (email: string) => {
    const data = getEmailVerificationData(email);
    if (data?.sentAt) {
      setShowOTPForm(true);
      const remaining = getRemainingCooldown(email);
      setCooldownSeconds(remaining);

      // Démarrer le timer si nécessaire
      if (remaining > 0) {
        startCooldownTimer();
      }
    }
  };

  // Gestion du timer de cooldown
  const startCooldownTimer = () => {
    const interval = setInterval(() => {
      setCooldownSeconds((prev) => {
        const newValue = prev - 1;
        if (newValue <= 0) {
          clearInterval(interval);
          return 0;
        }
        return newValue;
      });
    }, 1000);
  };

  // Envoyer le code de vérification
  const sendVerificationCode = async (email: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      const result = await new Promise<boolean>((resolve, reject) => {
        authClient.emailOtp.sendVerificationOtp(
          {
            email,
            type: "email-verification",
          },
          {
            onSuccess: () => {
              const now = Date.now();
              saveEmailVerificationData(email, now);
              setShowOTPForm(true);
              setCooldownSeconds(EMAIL_COOLDOWN_SECONDS);
              startCooldownTimer();
              toast.success("Code de vérification envoyé avec succès");
              resolve(true);
            },
            onError: (error) => {
              toast.error("Erreur lors de l'envoi du code de vérification", {
                description: error.error.message,
              });
              reject(error);
            },
          }
        );
      });

      return result;
    } catch (error) {
      console.error("Failed to send verification code:", error);
      toast.error("Erreur lors de l'envoi du code");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Renvoyer le code avec vérification du cooldown
  const resendCode = async (email: string): Promise<boolean> => {
    const remaining = getRemainingCooldown(email);
    if (remaining > 0) {
      toast.error(`Veuillez attendre ${remaining} secondes avant de renvoyer un code`);
      return false;
    }

    return sendVerificationCode(email);
  };

  // Vérifier le code OTP
  const verifyCode = async (email: string, otp: string, onSuccess?: () => void): Promise<boolean> => {
    setIsLoading(true);

    try {
      const result = await new Promise<boolean>((resolve, reject) => {
        authClient.emailOtp.verifyEmail(
          { email, otp },
          {
            onSuccess: () => {
              toast.success("Email vérifié avec succès");
              clearEmailVerificationData(email);
              onSuccess?.();
              resolve(true);
            },
            onError: (error) => {
              if (error.error.code === "EXPIRED_OTP") {
                toast.error("Le code de vérification a expiré", {
                  action: { label: "Renvoyer", onClick: () => resendCode(email) },
                  duration: Infinity,
                });
              } else if (error.error.code === "TOO_MANY_ATTEMPTS") {
                toast.error("Trop de demandes envoyées. Veuillez renvoyer un code.", {
                  action: { label: "Renvoyer", onClick: () => resendCode(email) },
                  duration: Infinity,
                });
              } else if (error.error.code === "INVALID_OTP") {
                toast.error("Code de vérification invalide", {
                  description: "Veuillez vérifier le code et réessayer.",
                });
              } else {
                toast.error("Erreur lors de la vérification du code", {
                  description: error.error.message,
                });
              }
              reject(error);
            },
          }
        );
      });

      return result;
    } catch (error) {
      console.error("Email verification failed:", error);
      toast.error("La vérification de l'email a échoué", {
        description: "Une erreur s'est produite lors de la vérification de votre email.",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // État
    isLoading,
    cooldownSeconds,
    showOTPForm,

    // Actions
    initializeFromEmail,
    sendVerificationCode,
    resendCode,
    verifyCode,
    getRemainingCooldown,
  };
}
